import { ReactComponent as PlayButtonSVG } from '@/assets/images/svg/buttons/play-button.svg';
import Image from '@/components/Image';
import { COLORS } from '@/styles/color';
import { hexToRGBA } from '@/styles/color/utils';
import { remCalc } from '@/styles/typography/utils';
import { forwardRef, ReactEventHandler, useEffect, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';

// #region - Components

const PlayButton = styled(PlayButtonSVG)`
  position: absolute;
  top: calc(50% - ${remCalc(68 / 2)});
  left: calc(50% - ${remCalc(68 / 2)});
  width: ${remCalc(68)};
  z-index: 2;
  transform: scale(1);
  transform-origin: center;
  transition: transform 0.4s ease-in-out, box-shadow 0.4s ease-in-out;
  box-shadow: 0 0 10px 0 ${hexToRGBA(COLORS.BLACK, 0.25)};
  border-radius: 100%;
  pointer-events: none;
`;

interface FadeProps {
  show: boolean;
}

const Fade = styled.div<FadeProps>`
  position: absolute;
  left: 0;
  bottom: 0;
  z-index: 2;

  width: 100%;
  height: 45%;

  background: linear-gradient(0deg, ${COLORS.BLACK} 0%, transparent 100%);
  opacity: 0.35;
  transition: opacity 0.4s ease-in-out;

  pointer-events: none;

  ${(props) =>
    !props.show &&
    css`
      opacity: 0;
    `}
`;

interface VideoWrapperProps {
  showPointer: boolean;
  videoWidth: number;
  videoHeight: number;
}

const VideoWrapper = styled.div<VideoWrapperProps>`
  position: relative;
  height: 0;
  padding-bottom: ${(props) => (props.videoHeight / props.videoWidth) * 100}%;
  background-color: #000;
  overflow: hidden;

  transition: padding-bottom 0.15s ease-in-out;

  img {
    object-fit: cover;
    object-position: center;
  }

  ${(props) =>
    props.showPointer &&
    css`
      cursor: pointer;
    `}

  &:hover {
    ${Fade} {
      opacity: 0;
    }

    ${PlayButton} {
      transform: scale(1.1);
      box-shadow: 0 0 15px 0 ${hexToRGBA(COLORS.BLACK, 0.15)};
    }
  }

  > div:first-child {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
`;

// #endregion - Components

interface VideoProps extends PropsWithClassName {
  videoUrl: string;
  captionsUrl?: string | null;
  coverSrc?: string;
  onInitialPlay?: () => void;
  showPlayButton?: boolean;
  className?: string;
}

const Video = forwardRef<HTMLVideoElement, VideoProps>((props, ref) => {
  const [played, setPlayed] = useState(false);
  const [showCover, setShowCover] = useState(!!props.coverSrc);
  const [width, setWidth] = useState(1600);
  const [height, setHeight] = useState(900);
  const [hasAudio, setHasAudio] = useState(true);

  const shouldAutoplay = useMemo(() => !!props.coverSrc, []);

  const onPlay = () => {
    if (played) return;
    setPlayed(true);
    if (props.onInitialPlay) props.onInitialPlay();
  };

  const onVideoLoad: ReactEventHandler<HTMLVideoElement> = (e) => {
    const video = e.target as HTMLVideoElement;

    if (!video || !video.videoWidth) return;

    setWidth(video.videoWidth ?? width);
    setHeight(video.videoHeight ?? height);

    try {
      const videoHasAudio =
        /* @ts-ignore */
        video.mozHasAudio || Boolean(video.webkitAudioDecodedByteCount) || Boolean(video.audioTracks?.length);

      if (!videoHasAudio) {
        setHasAudio(false);
        video.play().catch((e) => {
          // Commonly a NotAllowedError, which we can just silently ignore
        });
      }
    } catch (e) {
      //
    }
  };

  /* Reset when video changes */
  useEffect(() => {
    setPlayed(false);
    setShowCover(!!props.coverSrc);
  }, [props.videoUrl, props.coverSrc]);

  return (
    <VideoWrapper className={props.className} showPointer={showCover} videoWidth={width} videoHeight={height}>
      {showCover ? (
        <Image
          src={props.coverSrc!}
          alt=""
          onClick={() => {
            setShowCover(false);
          }}
          fill
        />
      ) : (
        <video
          ref={ref}
          controls
          loop={!hasAudio}
          muted={!hasAudio}
          autoPlay={shouldAutoplay}
          onLoadedData={onVideoLoad}
          onLoad={onVideoLoad}
          onPlay={onPlay}
          playsInline
        >
          <source src={props.videoUrl} />
          {props.captionsUrl && <track label="English" kind="subtitles" srcLang="en" src={props.captionsUrl} default />}
        </video>
      )}

      <Fade show={showCover} />

      {props.showPlayButton && showCover && <PlayButton />}
    </VideoWrapper>
  );
});

Video.displayName = 'Video';

Video.defaultProps = {
  showPlayButton: false,
};

export default Video;
